import React, { useState, useLayoutEffect, useRef } from 'react';
import { useTheme, makeStyles, Button, IconButton, Box, Typography, Popover, Grid, Paper } from '@material-ui/core';
import { ReactComponent as DownloadIcon } from '../../resources/images/download.svg';
import { ReactComponent as PrintIcon } from '../../resources/images/printer.svg';
import { ReactComponent as InfoButton } from '../../resources/images/info.svg';
import DateOfDiagnosisBadge from '../../resources/images/rocket-badge-big.svg';
import ImagingBadge from '../../resources/images/imaging-big-badge.svg';
import BloodBadge from '../../resources/images/blood-badge.svg';
import EndOftreatmentBadge from '../../resources/images/circle-check-big.svg';
import { SizeSelector } from '../utilities/layouts/SizeSelector';
import { styleBreakMobile } from '../utilities/layouts/Globals';
import { COLOR_WHITE, COLOR_GREY } from '../utilities/MaterialWatchman';
import { CalendarProgressDesktop, CalendarProgressMobile } from '../calendar/CalendarProgress';
import CalendarIconsInfo from '../calendar/CalendarIconsInfo';
import TutorialFloatingGrid from './TutorialFloatingGrid';
import clsx from 'clsx';

const useStyles = makeStyles(theme => ({
    stageLabel: {
        fontSize: theme.spacing(2.5),
        fontWeight: 600,
        [styleBreakMobile(theme)]: {
            fontSize: theme.spacing(2),
            marginTop: theme.spacing(1),
            marginBottom: theme.spacing(1),
        },
    },
    calendarControls: {
        display: 'flex',
        justifyContent: 'space-between',
        width: '100%',
        alignItems: 'center',
    },
    pdfButton: {
        borderRadius: '50%',
        '& *': {
            stroke: theme.palette.primary.dark,
        },
    },
    moreInfoButton: {
        marginLeft: theme.spacing(1.5),
        height: theme.spacing(3.625),
        padding: '6px 11px 6px 12px',
        pointerEvents: 'none',
        border: `1px solid ${theme.palette.primary.dark}`,
        borderRadius: theme.spacing(0.5),
    },
    infoButton: {
        height: theme.spacing(1.75),
        width: theme.spacing(1.75),
        marginRight: theme.spacing(1),
    },
    infoText: {
        fontWeight: theme.spacing(75),
        fontSize: theme.spacing(1.5),
    },
    popoverStyle: {
        boxShadow: '0px 10px 20px rgba(0, 0, 0, 0.15)',
        marginTop: theme.spacing(0.5),
        borderRadius: '25px 0px 25px 25px',
    },
    tableContainer: {
        marginTop: theme.spacing(0.5),
        width: '100%',
        height: theme.spacing(11) + 2,
        boxShadow: 'none',
    },
    tableGrid: {
        height: 'inherit',
        alignItems: 'center',
        display: 'flex',
        justifyContent: 'center',
    },
    tableFooter: {
        marginTop: theme.spacing(2),
        fontSize: theme.spacing(1.5),
    },
    monthCell: {
        textAlign: 'center',
        background: COLOR_WHITE,
        padding: `${theme.spacing(1) + 1}px ${theme.spacing(0.5) + 1.5}px`,
        fontWeight: 400,
        fontSize: theme.spacing(1.5),
        lineHeight: '125%',
        width: '100%',
        maxWidth: theme.spacing(11) + 2,
    },
    monthRow: {
        display: 'flex',
        width: '100%',
        height: theme.spacing(4),
        marginTop: theme.spacing(2.5),
    },
    currentDate: {
        fontWeight: 700,
        fontSize: theme.spacing(1.5),
    },
    pastMonth: {
        background: COLOR_GREY.light,
        width: '100%',
        maxWidth: theme.spacing(11) + 2,
        height: 'inherit',
    },
    currentMonth: {
        border: `${theme.spacing(0.5) - 1}px solid ${theme.palette.primary.main}`,
        width: '100%',
        maxWidth: theme.spacing(11) + 2,
        height: 'inherit',
    },
}));

export const MobileStageLabel = ({ stage }) => {
    const classes = useStyles();

    return (
        <div className={classes.calendarControls}>
            <Box>
                <Typography className={classes.stageLabel}>{stage}</Typography>
            </Box>
        </div>
    );
};

const months = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];

function MonthCell({ month }) {
    const classes = useStyles();
    return (
        <div
            className={clsx(classes.monthCell, {
                [classes.currentDate]: months[2] === month,
            })}
        >
            {month ?? ''}
        </div>
    );
}

function MonthRow() {
    const classes = useStyles();
    return (
        <div className={classes.monthRow}>
            <MonthCell />
            {months.map((month, indx) => (
                <MonthCell month={month} key={indx} />
            ))}
        </div>
    );
}

function TutorialMockCalendar({ tutorialCurrentPage, complexGridInfo, type }) {
    const classes = useStyles();
    const customRef = useRef(null);
    const [x, setX] = useState();

    const tableFooterText = 'Note: This calendar does not reflect your personal surveillance journey';

    // This function calculate X-Cordinate (xCord)
    const getPosition = () => {
        const x = customRef?.current?.offsetLeft;
        tutorialCurrentPage >= 10 ? setX(x - 408) : setX(x - 8);
    };

    useLayoutEffect(() => {
        getPosition();
        window.addEventListener('resize', getPosition);
        return () => {
            window.removeEventListener('resize', getPosition);
        };
    });

    return (
        <>
            <MonthRow />
            <Box sx={{ flexGrow: 0 }} component={Paper} className={classes.tableContainer}>
                <Grid container className={classes.tableGrid}>
                    <Grid item xs className={classes.tableGrid}>
                        <Typography className={classes.currentDate}>2022</Typography>
                    </Grid>
                    <Grid ref={tutorialCurrentPage === 7 ? customRef : null} item xs className={classes.tableGrid}>
                        <div className={classes.pastMonth} />
                    </Grid>
                    <Grid item xs className={classes.tableGrid} />
                    <Grid ref={tutorialCurrentPage === 8 ? customRef : null} item xs className={classes.tableGrid}>
                        <div className={classes.currentMonth} />
                    </Grid>
                    <Grid item xs className={classes.tableGrid} />
                    <Grid ref={tutorialCurrentPage === 9 ? customRef : null} item xs className={classes.tableGrid}>
                        <img src={DateOfDiagnosisBadge} alt="Date of Diagnosis badge" aria-labelledby="Date of Diagnosis badge" />
                    </Grid>
                    <Grid item xs className={classes.tableGrid} />
                    <Grid ref={tutorialCurrentPage === 10 ? customRef : null} item xs className={classes.tableGrid}>
                        <img src={ImagingBadge} alt="Imaging badge" aria-labelledby="Imaging badge" />
                    </Grid>
                    <Grid item xs className={classes.tableGrid} />
                    <Grid ref={tutorialCurrentPage === 11 ? customRef : null} item xs className={classes.tableGrid}>
                        <img src={BloodBadge} alt="Blood badge" aria-labelledby="Blood badge" />
                    </Grid>
                    <Grid item xs className={classes.tableGrid} />
                    <Grid item xs className={classes.tableGrid} />
                    <Grid ref={tutorialCurrentPage === 12 ? customRef : null} item xs className={classes.tableGrid}>
                        <img src={EndOftreatmentBadge} alt="End of Treatment badge" aria-labelledby="End of Treatment badge" />
                    </Grid>
                </Grid>
            </Box>
            <Typography className={classes.tableFooter}>{tableFooterText}</Typography>
            <TutorialFloatingGrid tutorialCurrentPage={tutorialCurrentPage} complexGridInfo={complexGridInfo} type={type} xCord={x} />
        </>
    );
}

const TutorialCalendarLayout = ({ tutorialCurrentPage, dateOfDiagnosis, endOfTreatementDate, stage, complexGridInfo, type }) => {
    const classes = useStyles();
    const theme = useTheme();
    const customRef = useRef(null);
    const [openPopover, setOpenPopover] = useState(false);

    const HIGHLIGHTED_BUTTON_COLOUR = theme.palette.primary.light;
    const SELECTED_BUTTON_COLOUR = theme.palette.primary.dark;
    const id = openPopover ? 'simple-popover' : undefined;

    useLayoutEffect(() => {
        tutorialCurrentPage === 6 ? setOpenPopover(true) : setOpenPopover(false);
    }, [tutorialCurrentPage]);

    const MobileCalendarControls = <MobileStageLabel stage={stage} />;
    const DesktopCalendarControls = (
        <div className={classes.calendarControls}>
            <Box>
                <Typography className={classes.stageLabel} style={{ opacity: tutorialCurrentPage === 2 ? 1 : 0.4 }}>
                    {stage}
                </Typography>
            </Box>
            <Box>
                <IconButton
                    aria-label="download"
                    component="span"
                    style={{
                        background: tutorialCurrentPage === 3 && HIGHLIGHTED_BUTTON_COLOUR,
                        pointerEvents: 'none',
                        opacity: tutorialCurrentPage === 3 ? 1 : 0.4,
                    }}
                >
                    <DownloadIcon aria-labelledby="download" className={classes.pdfButton} />
                </IconButton>
                <IconButton
                    aria-label="print"
                    component="span"
                    style={{
                        background: tutorialCurrentPage === 4 && HIGHLIGHTED_BUTTON_COLOUR,
                        pointerEvents: 'none',
                        opacity: tutorialCurrentPage === 4 ? 1 : 0.4,
                    }}
                >
                    <PrintIcon aria-labelledby="print" className={classes.pdfButton} />
                </IconButton>
                <Button
                    aria-label="more-information"
                    component="span"
                    className={classes.moreInfoButton}
                    style={{
                        background: tutorialCurrentPage === 5 ? COLOR_WHITE : tutorialCurrentPage === 6 ? SELECTED_BUTTON_COLOUR : 'none',
                        color: tutorialCurrentPage === 6 ? COLOR_WHITE : SELECTED_BUTTON_COLOUR,
                        opacity: tutorialCurrentPage === 5 || tutorialCurrentPage === 6 ? 1 : 0.4,
                    }}
                    ref={customRef}
                >
                    <InfoButton className={classes.infoButton} />
                    <Typography className={classes.infoText} style={{ color: 'inherit' }}>
                        Legend
                    </Typography>
                </Button>
                <Popover
                    id={id}
                    open={openPopover}
                    anchorEl={customRef.current}
                    anchorOrigin={{
                        vertical: 'bottom',
                        horizontal: 'right',
                    }}
                    transformOrigin={{
                        vertical: 'top',
                        horizontal: 'right',
                    }}
                    classes={{ paper: classes.popoverStyle }}
                >
                    <CalendarIconsInfo />
                </Popover>
            </Box>
        </div>
    );

    const MobileCalendarProgress = <CalendarProgressMobile dateOfDiagnosis={dateOfDiagnosis} endOfTreatmentDate={endOfTreatementDate} />;
    const DesktopCalendarProgress = (
        <CalendarProgressDesktop dateOfDiagnosis={dateOfDiagnosis} endOfTreatmentDate={endOfTreatementDate} tutorialCurrentPage={tutorialCurrentPage} />
    );

    return (
        <>
            <SizeSelector mobile={MobileCalendarControls} desktop={DesktopCalendarControls} />
            <SizeSelector mobile={MobileCalendarProgress} desktop={DesktopCalendarProgress} />
            {tutorialCurrentPage > 6 && <TutorialMockCalendar tutorialCurrentPage={tutorialCurrentPage} complexGridInfo={complexGridInfo} type={type} />}
        </>
    );
};

export default TutorialCalendarLayout;
